                  import * as THREE from 'three';
                  import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
                  import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
                  import { FontLoader } from 'three/addons/loaders/FontLoader.js';
                  import { TextGeometry } from 'three/addons/geometries/TextGeometry.js';
                  import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';

                  (function () {
                    const cfg = window.namechainPreviewConfig || {};
                    const sectionId = cfg.sectionId;
                    const glbUrl = cfg.glbUrl || '';
                    const fontUrl = cfg.fontUrl || '';
                    if (!glbUrl) return;

                    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

                    const container = document.querySelector('.price-per-item__container');
                    if (!container) return;
                    container.style.flexDirection = 'column';
                    container.style.alignItems = 'start';

                    const rect = container.getBoundingClientRect();
                    const canvasSize = rect.width || 400;

                    const canvas = document.createElement('canvas');
                    const loaderEl = document.createElement('div');
                    loaderEl.textContent = 'Loading…';
                    loaderEl.style.cssText = 'position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-size:13px;color:#888;pointer-events:none;';
                    const wrap = document.createElement('div');
                    wrap.style.cssText = 'position:relative;width:100%;';
                    wrap.appendChild(canvas);
                    wrap.appendChild(loaderEl);

                    const disclaimer = document.createElement('p');
                    disclaimer.className = 'charm-builder__disclaimer';
                    disclaimer.textContent = 'Charms shown in preview images must be added to your cart separately to achieve the customized look pictured.';
                    disclaimer.style.cssText = 'margin:0.5rem 0 0;font-size:0.78rem;line-height:1.35;color:rgba(var(--color-foreground),0.7);font-style:italic;text-align:center;max-width:500px;align-self:center;';

                    container.prepend(disclaimer);
                    container.prepend(wrap);

                    const textInput = document.getElementById('your-name-for-the-pendant');

                    const renderer = new THREE.WebGLRenderer({
                      canvas,
                      antialias: true,
                      alpha: false,
                      powerPreference: isMobile ? 'low-power' : 'high-performance',
                      precision: 'highp',
                    });
                    renderer.outputColorSpace = THREE.SRGBColorSpace;
                    renderer.toneMapping = THREE.ACESFilmicToneMapping;
                    renderer.toneMappingExposure = 0.92;
                    renderer.shadowMap.enabled = !isMobile;
                    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
                    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
                    renderer.setSize(canvasSize, canvasSize);

                    window.addEventListener('resize', () => {
                      const r = container.getBoundingClientRect();
                      const s = r.width || 400;
                      renderer.setSize(s, s);
                    });

                    const scene = new THREE.Scene();
                    scene.background = new THREE.Color(0xffffff);

                    // PMREM room env → gold metal reflects bright room, no black/dark patches
                    const pmrem = new THREE.PMREMGenerator(renderer);
                    scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
                    scene.environmentIntensity = 1.0;

                    const camera = new THREE.PerspectiveCamera(25, 1, 0.1, 10000);
                    camera.position.set(0, 5, 35);
                    scene.add(camera);

                    // matched to charm-builder.js lighting rig (env map does most lighting; direct lights softened)
                    scene.add(new THREE.AmbientLight(0xffffff, 0.3));
                    const key = new THREE.DirectionalLight(0xffffff, 2);
                    key.position.set(3, 4, 3);
                    scene.add(key);
                    const fill = new THREE.DirectionalLight(0xfff0e0, 1);
                    fill.position.set(-3, 1, -2);
                    scene.add(fill);
                    const back = new THREE.DirectionalLight(0xffffff, 0.8);
                    back.position.set(0, 2, -4);
                    scene.add(back);
                    // point light to brighten chain — position + intensity scaled ~14x to namechain scene
                    const pt = new THREE.PointLight(0xffffff, 85.6 * 196);
                    pt.position.set(0, 28, 84);
                    scene.add(pt);

                    const controls = new OrbitControls(camera, canvas);
                    controls.target.set(0, 5, 0);
                    controls.maxDistance = 30;
                    controls.minDistance = 10;
                    controls.enableDamping = true;
                    controls.enablePan = false;

                    const MatClass = isMobile ? THREE.MeshStandardMaterial : THREE.MeshPhysicalMaterial;
                    const goldMat = new MatClass({ metalness: 0.96, roughness: 0.42, color: new THREE.Color(0xd4af37) });
                    const silverMat = new MatClass({ metalness: 0.96, roughness: 0.26 });
                    let currentMetal = 'gold';

                    // Invisible reference box — text scaling anchor, same coords as original viewer
                    const refBox = new THREE.Mesh(new THREE.BoxGeometry(5.7, 2), new THREE.MeshStandardMaterial({ transparent: true, opacity: 0 }));
                    refBox.userData.skipMetal = true; // invisible text-scale anchor, never recolor
                    refBox.position.set(-0.35, 1.7, 1.7);
                    scene.add(refBox);
                    const refBox3 = new THREE.Box3().setFromObject(refBox);

                    let textMesh = null;
                    let pinyonFont = null;

                    function isLongEnd(n) { return ['l','d','b','f'].includes(n[n.length-1]); }
                    function isHalfLongEnd(n) { return n[n.length-1] === 't'; }

                    function generateText(name) {
                      if (!pinyonFont || !name) return;
                      if (textMesh) { scene.remove(textMesh); textMesh.geometry.dispose(); textMesh.material.dispose(); textMesh = null; }
                      name = name.slice(0, 9);
                      const geo = new TextGeometry(name, {
                        font: pinyonFont, size: 2,
                        height: isMobile ? 0.01 : 0.03,
                        curveSegments: isMobile ? 4 : 12,
                        bevelEnabled: !isMobile,
                        bevelThickness: 0.03, bevelSize: 0.02, bevelOffset: 0, bevelSegments: 2
                      });
                      textMesh = new THREE.Mesh(geo, currentMetal === 'gold' ? goldMat : silverMat);
                      textMesh.castShadow = !isMobile;
                      textMesh.position.z = 1.73;
                      const tb = new THREE.Box3().setFromObject(textMesh);
                      let sc = refBox3.max.x / tb.max.x * 2.35;
                      if (isLongEnd(name)) sc *= 1.1;
                      else if (isHalfLongEnd(name)) sc *= 1.05;
                      textMesh.scale.setScalar(sc);
                      textMesh.position.x = -tb.max.x / 2 * sc - 0.3 / sc + (name.length > 7 ? 0.1 : 0);
                      if (isLongEnd(name)) textMesh.position.x += sc / 3;
                      else if (isHalfLongEnd(name)) textMesh.position.x += sc / 6;
                      textMesh.position.y = 1.7;
                      scene.add(textMesh);
                    }

                    function toggleMetal(metal) {
                      currentMetal = metal;
                      const mat = metal === 'gold' ? goldMat : silverMat;
                      scene.traverse((obj) => { if (obj.isMesh && obj.material && !obj.userData.skipMetal) { obj.material = obj.material.clone(); obj.material.copy(mat); } });
                      if (textMesh) textMesh.material = mat.clone();
                    }

                    function metalFromText(t) {
                      t = (t || '').toLowerCase();
                      if (t.includes('silver')) return 'silver';
                      if (t.includes('gold')) return 'gold';
                      return '';
                    }
                    // Variant pickers live in <variant-selects>/<variant-radios> OUTSIDE the
                    // form (linked via form= attr). Query by section id, not the form.
                    function readShopifyMetal() {
                      const roots = [
                        document.getElementById('variant-radios-' + sectionId),
                        document.getElementById('variant-selects-' + sectionId),
                        document.getElementById('product-form-' + sectionId),
                      ].filter(Boolean);
                      let txt = '';
                      roots.forEach((root) => {
                        root.querySelectorAll('input[type="radio"]:checked').forEach((el) => {
                          const m = metalFromText(el.value); if (m) txt = m;
                        });
                        root.querySelectorAll('select').forEach((s) => {
                          const m = metalFromText(s.value); if (m) txt = m;
                        });
                      });
                      if (txt) return txt;
                      const idInput = document.querySelector('#product-form-' + sectionId + ' input[name="id"], #product-form-' + sectionId + ' select[name="id"]');
                      const vid = idInput && String(idInput.value);
                      const root = document.getElementById('variant-selects-' + sectionId) || document.getElementById('variant-radios-' + sectionId);
                      const json = root && root.querySelector('script[type="application/json"]');
                      if (vid && json) {
                        try {
                          const variants = JSON.parse(json.textContent);
                          const v = variants.find((x) => String(x.id) === vid);
                          if (v) return metalFromText((v.title || '') + ' ' + (v.options || []).join(' '));
                        } catch (e) {}
                      }
                      return '';
                    }

                    const TEX_KEYS = ['map','normalMap','roughnessMap','metalnessMap','aoMap','emissiveMap','lightMap','bumpMap'];
                    const gltfLoader = new GLTFLoader();

                    gltfLoader.load(glbUrl, (gltf) => {
                      scene.add(gltf.scene);
                      gltf.scene.traverse((child) => {
                        child.castShadow = false;
                        child.receiveShadow = false;
                        if (child.isMesh) {
                          if (isMobile) { TEX_KEYS.forEach(k => { if (child.material[k]) { child.material[k].dispose(); child.material[k] = null; } }); child.material.needsUpdate = true; }
                          child.material.color = new THREE.Color(0xd4af37);
                          child.material.metalness = 0.96;
                          child.material.roughness = 0.47;
                          child.material.envMapIntensity = 0.8;
                          child.material.needsUpdate = true;
                        }
                      });
                      gltf.scene.rotateY(-Math.PI / 2 - Math.PI / 4.2);

                      if (!fontUrl) { loaderEl.style.display = 'none'; return; }
                      new FontLoader().load(fontUrl, (font) => {
                        pinyonFont = font;
                        loaderEl.style.display = 'none';
                        const initName = textInput ? (textInput.value || 'aaliyah') : 'aaliyah';
                        generateText(initName);
                        const m0 = readShopifyMetal();
                        if (m0) toggleMetal(m0);
                        // Picker change events bubble to document (picker sits outside form).
                        const onVariantChange = () => {
                          const m = readShopifyMetal();
                          if (m && m !== currentMetal) toggleMetal(m);
                        };
                        document.addEventListener('change', onVariantChange);
                        document.addEventListener('variant:change', onVariantChange);
                      }, undefined, (err) => { console.error('font', err); loaderEl.style.display = 'none'; });
                    }, undefined, (err) => { console.error('glb', err); loaderEl.textContent = 'Failed to load model.'; });

                    if (textInput) {
                      let timer;
                      textInput.addEventListener('keyup', () => {
                        clearTimeout(timer);
                        timer = setTimeout(() => {
                          if (textInput.value.length > 9) textInput.value = textInput.value.slice(0, 9);
                          if (textInput.value.length >= 2) generateText(textInput.value);
                        }, 1000);
                      });
                      textInput.addEventListener('keydown', () => clearTimeout(timer));
                    }

                    let rafId = null, ctxLost = false;
                    function tick() { if (ctxLost) return; controls.update(); renderer.render(scene, camera); rafId = requestAnimationFrame(tick); }
                    canvas.addEventListener('webglcontextlost', (e) => { e.preventDefault(); ctxLost = true; if (rafId) cancelAnimationFrame(rafId); });
                    canvas.addEventListener('webglcontextrestored', () => { ctxLost = false; tick(); });
                    document.addEventListener('visibilitychange', () => { if (document.hidden) { if (rafId) cancelAnimationFrame(rafId); } else if (!ctxLost) tick(); });
                    tick();
                  })();
